import { useState } from "react";
import { Plus, Pencil, BookOpen, X, Layers } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg, useApi } from "../../lib/api";
import { PageHeader, Badge, SkeletonGrid, ErrorState, EmptyState } from "../../components/common";
import { Stagger, AnimatedCard } from "../../components/motion";
import { Field, Input, Select, STATUS_OPTS, Toolbar, IconBtn, FormDialog, ConfirmDelete } from "../../components/admin/kit";

const EMPTY = { title: "", description: "", cover_url: "", category: "", duration: "", status: "active", modules: [] };
const TYPES = [["text", "Metin"], ["video", "Video URL / Embed"], ["pdf", "PDF URL"], ["image", "Görsel URL"]];
const uid = () => Math.random().toString(36).slice(2);
const newLesson = () => ({ _k: uid(), title: "", type: "text", url: "", content: "" });

function ModulesEditor({ modules, onChange }) {
  const upd = (mi, patch) => onChange(modules.map((m, i) => (i === mi ? { ...m, ...patch } : m)));
  const updL = (mi, li, patch) => upd(mi, { lessons: modules[mi].lessons.map((l, i) => (i === li ? { ...l, ...patch } : l)) });
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold">Modüller ve Dersler</p>
        <button type="button" onClick={() => onChange([...modules, { _k: uid(), title: "", lessons: [newLesson()] }])} className="btn-ghost inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold" data-testid="add-module-btn"><Plus className="h-3.5 w-3.5" /> Modül Ekle</button>
      </div>
      {modules.length === 0 && <p className="rounded-xl bg-slate-50 p-4 text-center text-sm text-slate-400">Henüz modül eklenmedi.</p>}
      {modules.map((m, mi) => (
        <motion.div key={m.id || m._k} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-slate-200 bg-slate-50/50 p-4" data-testid={`module-editor-${mi}`}>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-slate-400">M{mi + 1}</span>
            <Input value={m.title} onChange={(e) => upd(mi, { title: e.target.value })} placeholder="Modül başlığı" required data-testid={`module-title-input-${mi}`} />
            <IconBtn type="button" icon={X} label="Modülü kaldır" danger onClick={() => onChange(modules.filter((_, i) => i !== mi))} data-testid={`remove-module-${mi}`} />
          </div>
          <div className="mt-3 space-y-3 pl-0 sm:pl-6">
            {m.lessons.map((l, li) => (
              <div key={l.id || l._k} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`lesson-editor-${mi}-${li}`}>
                <div className="grid gap-2 sm:grid-cols-[1fr_170px_auto]">
                  <Input value={l.title} onChange={(e) => updL(mi, li, { title: e.target.value })} placeholder="Ders başlığı" required data-testid={`lesson-title-input-${mi}-${li}`} />
                  <Select value={l.type} onChange={(e) => updL(mi, li, { type: e.target.value })} options={TYPES} data-testid={`lesson-type-select-${mi}-${li}`} />
                  <IconBtn type="button" icon={X} label="Dersi kaldır" danger onClick={() => upd(mi, { lessons: m.lessons.filter((_, i) => i !== li) })} />
                </div>
                {l.type !== "text" && <Input className="field mt-2" value={l.url} onChange={(e) => updL(mi, li, { url: e.target.value })} placeholder="https://..." data-testid={`lesson-url-input-${mi}-${li}`} />}
                <textarea className="field mt-2 min-h-[70px]" value={l.content} onChange={(e) => updL(mi, li, { content: e.target.value })} placeholder="Ders metni / açıklama" data-testid={`lesson-content-input-${mi}-${li}`} />
              </div>
            ))}
            <button type="button" onClick={() => upd(mi, { lessons: [...m.lessons, newLesson()] })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand)] hover:underline" data-testid={`add-lesson-btn-${mi}`}><Plus className="h-3.5 w-3.5" /> Ders Ekle</button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export default function AdminCourses() {
  const { data, loading, error, reload } = useApi("/admin/courses");
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const { id, title, description, cover_url, category, duration, status, modules } = form;
      const body = { title, description, cover_url, category, duration, status, modules };
      if (id) await api.put(`/admin/courses/${id}`, body);
      else await api.post("/admin/courses", body);
      toast.success(id ? "Eğitim güncellendi." : "Eğitim oluşturuldu.");
      setForm(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const del = async (c) => {
    try {
      const r = await api.delete(`/admin/courses/${c.id}`);
      toast.success(r.data.archived ? "Eğitim atamaları olduğu için pasife alındı." : "Eğitim silindi.");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const list = (data || []).filter((c) => c.title.toLocaleLowerCase("tr-TR").includes(q.toLocaleLowerCase("tr-TR")));
  return (
    <div>
      <PageHeader eyebrow="Yönetim" title="Eğitimler" desc="Eğitim programları, modüller ve dersler." />
      <Toolbar q={q} setQ={setQ} placeholder="Eğitim ara..." action={
        <button onClick={() => setForm(EMPTY)} className="btn-primary inline-flex h-11 items-center gap-2 rounded-xl px-5 text-sm font-semibold" data-testid="add-course-btn"><Plus className="h-4 w-4" /> Yeni Eğitim</button>
      } />
      {error ? <ErrorState message={error} onRetry={reload} /> : loading ? <SkeletonGrid count={3} className="h-72" /> : list.length === 0 ? (
        <EmptyState icon={BookOpen} title="Eğitim bulunamadı." desc="Yeni bir eğitim oluşturarak başlayın." />
      ) : (
        <Stagger className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" gap={0.07}>
          {list.map((c) => (
            <AnimatedCard key={c.id} className="group overflow-hidden" data-testid={`admin-course-card-${c.id}`}>
              <div className="relative aspect-[16/8] overflow-hidden bg-slate-100">
                {c.cover_url && <img src={c.cover_url} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
                <div className="absolute left-3 top-3 flex gap-1.5"><Badge status={c.status} />{c.is_demo && <Badge status="demo" />}</div>
              </div>
              <div className="p-5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{c.category || "—"}</p>
                <h3 className="mt-1 font-bold text-[var(--ink)]">{c.title}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Layers className="h-3.5 w-3.5" /> {c.module_count} modül · {c.lesson_count} ders</span>
                  <div className="flex gap-1">
                    <IconBtn icon={Pencil} label="Düzenle" onClick={() => setForm(c)} data-testid={`edit-course-${c.id}`} />
                    <ConfirmDelete label="Sil / Pasife Al" onConfirm={() => del(c)} testid={`delete-course-${c.id}`} />
                  </div>
                </div>
              </div>
            </AnimatedCard>
          ))}
        </Stagger>
      )}
      <FormDialog wide open={!!form} onClose={() => setForm(null)} title={form?.id ? "Eğitimi Düzenle" : "Yeni Eğitim"} onSubmit={save} saving={saving} testid="course-form-dialog">
        {form && (
          <>
            <Field label="Eğitim Adı"><Input value={form.title} onChange={set("title")} required data-testid="course-title-input" /></Field>
            <Field label="Açıklama"><textarea className="field min-h-[80px]" value={form.description} onChange={set("description")} data-testid="course-description-input" /></Field>
            <Field label="Kapak Görseli URL"><Input value={form.cover_url} onChange={set("cover_url")} placeholder="https://..." data-testid="course-cover-input" /></Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Kategori"><Input value={form.category} onChange={set("category")} data-testid="course-category-input" /></Field>
              <Field label="Süre"><Input value={form.duration} onChange={set("duration")} placeholder="Örn. 40 Saat" data-testid="course-duration-input" /></Field>
              <Field label="Durum"><Select value={form.status} onChange={set("status")} options={STATUS_OPTS} data-testid="course-status-select" /></Field>
            </div>
            <ModulesEditor modules={form.modules} onChange={(modules) => setForm({ ...form, modules })} />
          </>
        )}
      </FormDialog>
    </div>
  );
}
